import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './MediaCenter.module.css';
import API_BASE_URL from '../../config/api';

const MediaCenter = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [activeTab, setActiveTab] = useState('multimediaGallery');
  const [videos, setVideos] = useState([]);
  const [socialLinks, setSocialLinks] = useState([]);

  const tabs = [
    { id: 'multimediaGallery', key: 'multimediaGallery' },
    { id: 'socialMedia', key: 'socialMedia' }
  ];

  useEffect(() => {
    const controller = new AbortController();
    const lang = language === 'ar' ? 'ar' : 'en';

    const fetchVideos = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/media/videos?lang=${lang}`, { signal: controller.signal });
        const data = await res.json();
        setVideos(Array.isArray(data) ? data : data.data || []);
      } catch (error) {
        if (error.name !== 'AbortError') console.error('Failed to load videos', error);
      }
    };

    const fetchSocialLinks = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/media/social-links?lang=${lang}`, { signal: controller.signal });
        const data = await res.json();
        setSocialLinks(Array.isArray(data) ? data : data.data || []);
      } catch (error) {
        if (error.name !== 'AbortError') console.error('Failed to load social links', error);
      }
    };

    fetchVideos();
    fetchSocialLinks();

    return () => controller.abort();
  }, [language]);

  const getSocialIcon = (platform) => {
    const p = (platform || '').toLowerCase();
    if (p.includes('facebook')) {
      return <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>;
    }
    if (p.includes('youtube')) {
      return <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>;
    }
    if (p.includes('twitter') || p.includes('x')) {
      return <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>;
    }
    if (p.includes('linkedin')) {
      return <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>;
    }
    return <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8z"/>;
  };

  const getYoutubeEmbedUrl = (url) => {
    if (!url) return '';
    if (url.includes('/embed/')) return url;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([^&?/]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : url;
  };

  return (
    <div className={styles.mediaPage}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>{t('media.title')}</h1>
          <div className={styles.heroUnderline}></div>
        </div>
      </section>

      {/* Tabs Section */}
      <section className={styles.tabsSection}>
        <div className={styles.container}>
          <div className={styles.tabsContainer}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span>{t(`media.tabs.${tab.key}`)}</span>
                {activeTab === tab.id && <div className={styles.tabIndicator}></div>}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className={styles.contentSection}>
        <div className={styles.container}>
          {activeTab === 'multimediaGallery' ? (
            <div className={styles.videosSection}>
              <div className={styles.videosGrid}>
                {videos.map((video) => (
                  <div key={video.id} className={styles.videoCard}>
                    <div className={styles.videoWrapper}>
                      <iframe
                        src={getYoutubeEmbedUrl(video.youtube_url)}
                        title={video.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className={styles.videoIframe}
                      ></iframe>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : activeTab === 'socialMedia' ? (
            <div className={styles.socialMediaSection}>
              <div className={styles.socialMediaGrid}>
                {socialLinks.map((link) => (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.socialMediaCard}
                  >
                    <div className={styles.socialIconWrapper}>
                      <svg className={styles.socialIcon} viewBox="0 0 24 24" fill="currentColor">
                        {getSocialIcon(link.platform)}
                      </svg>
                    </div>
                    <h3 className={styles.socialTitle}>{link.platform}</h3>
                    <p className={styles.socialDescription}>{link.label}</p>
                    <div className={styles.socialLink}>
                      <span>{language === 'ar' ? 'زيارة' : 'Visit'}</span>
                      <svg viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
};

export default MediaCenter;
